import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import type { Portal } from "@/types";
import { FormProvider, TextBox } from "@/components/form";

export interface OverviewFormValues {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

interface OverviewTabProps {
  portal: Portal;
  onSave: (data: OverviewFormValues) => void;
  onStatusToggle: (active: boolean) => void;
  isUpdating: boolean;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ portal, onSave, onStatusToggle, isUpdating }) => {
  const active = portal.status === "active";
  const canToggle = portal.status === "active" || portal.status === "inactive";

  const handleToggle = (next: boolean) => {
    onStatusToggle(next);
  };

  const methods = useForm<OverviewFormValues>({
    defaultValues: {
      contact_name: portal.contact_name ?? "",
      contact_email: portal.contact_email ?? "",
      contact_phone: portal.contact_phone ?? "",
    },
  });

  useEffect(() => {
    methods.reset({
      contact_name: portal.contact_name ?? "",
      contact_email: portal.contact_email ?? "",
      contact_phone: portal.contact_phone ?? "",
    });
  }, [portal, methods]);

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{ p: 2, border: "1px solid", borderColor: "divider" }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>
                Operational status
              </Typography>
              <Chip
                label={active ? "Active" : "Inactive"}
                color={active ? "success" : "default"}
                size="small"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              When inactive, login is blocked and no API access is allowed for
              this portal. New portals default to inactive until you switch
              them on.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={active}
                disabled={!canToggle || isUpdating}
                onChange={(e) => handleToggle(e.target.checked)}
              />
            }
            label={active ? "Active" : "Inactive"}
            labelPlacement="start"
          />
        </Stack>
        {!canToggle && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Portal is currently <strong>{portal.status}</strong>. Toggle is
            disabled until provisioning completes and the portal reaches an
            operational state.
          </Alert>
        )}
      </Paper>

      <FormProvider methods={methods} onSubmit={onSave}>
        <Stack spacing={2}>
          <TextField
            label="Portal name"
            value={portal.name}
            disabled
            fullWidth
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextBox
                name="contact_name"
                label="Contact name"
                fullWidth
                disabled={isUpdating}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextBox
                name="contact_email"
                label="Contact email"
                fullWidth
                disabled={isUpdating}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextBox
                name="contact_phone"
                label="Contact phone"
                fullWidth
                disabled={isUpdating}
              />
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={isUpdating}>
              {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Save Overview"}
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default OverviewTab;
